import moment from 'moment'

function activityWindowUserKey(w) {
  const id = w.dashboardUserId ?? w.DashboardUserId
  return id == null || id === undefined ? 'unassigned' : String(id)
}

function eventMatchesTimelineFilter(ev, filterKey) {
  if (filterKey === 'all') {
    return true
  }
  const uid = ev.dashboardUserId ?? ev.DashboardUserId
  if (filterKey === 'unassigned') {
    return uid == null || uid === undefined
  }
  return String(uid) === filterKey
}

/**
 * Same as strict user id match, plus: events with no user id inside that user's activity windows.
 */
export function eventMatchesTimelineFilterWithSessionAttribution(
  ev,
  filterKey,
  activityWindowsForAttribution
) {
  if (filterKey === 'all') {
    return true
  }
  if (eventMatchesTimelineFilter(ev, filterKey)) {
    return true
  }
  const uid = ev.dashboardUserId ?? ev.DashboardUserId
  if (uid != null && uid !== undefined) {
    return false
  }
  if (filterKey === 'unassigned') {
    return true
  }
  const t = moment(ev.occurredAt ?? ev.OccurredAt)
  if (!t.isValid()) {
    return false
  }
  for (const w of activityWindowsForAttribution || []) {
    if (activityWindowUserKey(w) !== filterKey) {
      continue
    }
    const ws = moment(w.startAt ?? w.StartAt)
    const we = moment(w.endAt ?? w.EndAt)
    if (ws.isValid() && we.isValid() && t.isBetween(ws, we, null, '[]')) {
      return true
    }
  }
  return false
}

export function hasSuccessfulDataPullSince(events, sinceMoment) {
  if (!Array.isArray(events) || !sinceMoment?.isValid?.()) {
    return false
  }
  for (const ev of events) {
    const type = (ev.eventType ?? ev.EventType ?? '').toString().toLowerCase()
    const msg = (ev.message ?? ev.Message ?? '').toString().toLowerCase()
    const t = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!t.isValid() || t.isBefore(sinceMoment)) {
      continue
    }
    if (
      type.includes('datapullsucceeded') ||
      type.includes('data_pull_succeeded') ||
      msg.includes('pep_data_pull_ok')
    ) {
      return true
    }
  }
  return false
}

/**
 * Hide stale 403 banner when Hilton schedulers are healthy or recent pulls succeeded.
 */
export function shouldSuppressPepAuthorizationAlert(pepHealthCheckDisplay, timelineEvents) {
  if (
    pepHealthCheckDisplay?.onboardingState === 'Done' &&
    pepHealthCheckDisplay?.futureState === 'Active'
  ) {
    return true
  }
  return hasSuccessfulDataPullSince(timelineEvents, moment().subtract(48, 'hours'))
}

function normalizeWindowInterval(win) {
  const start = moment(win.startAt ?? win.StartAt)
  const end = moment(win.endAt ?? win.EndAt)
  if (!start.isValid() || !end.isValid()) {
    return null
  }
  return {
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    dashboardUserId: win.dashboardUserId ?? win.DashboardUserId,
    label: win.label ?? win.Label ?? 'Active reporting',
    id: win.id ?? win.Id
  }
}

/**
 * Merges overlapping or adjacent windows (gapMinutes apart) into continuous segments.
 */
export function mergeActivityWindowIntervals(windows, gapMinutes = 15) {
  const gap = Math.max(1, Number(gapMinutes) || 15)
  const parsed = (windows || [])
    .map(normalizeWindowInterval)
    .filter(Boolean)
    .sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf())

  if (parsed.length === 0) {
    return []
  }

  const merged = []
  let current = { ...parsed[0] }

  for (let i = 1; i < parsed.length; i += 1) {
    const next = parsed[i]
    const currEnd = moment(current.endAt)
    const nextStart = moment(next.startAt)
    if (nextStart.diff(currEnd, 'minutes') <= gap) {
      const nextEnd = moment(next.endAt)
      if (nextEnd.isAfter(currEnd)) {
        current.endAt = next.endAt
      }
      continue
    }
    merged.push(current)
    current = { ...next }
  }
  merged.push(current)

  return merged.map((w, index) => ({
    ...w,
    id: w.id ?? `merged-${index}-${w.startAt}`
  }))
}

/**
 * Builds session bars from raw timeline events (any ingest proves the app was alive).
 */
export function buildActivityWindowsFromEvents(
  userKey,
  eventsInRange,
  gapMinutes,
  activityWindowsForAttribution
) {
  const gap = Math.max(1, Number(gapMinutes) || 15)
  const times = []

  for (const ev of eventsInRange || []) {
    if (
      !eventMatchesTimelineFilterWithSessionAttribution(
        ev,
        userKey,
        activityWindowsForAttribution
      )
    ) {
      continue
    }
    const t = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!t.isValid()) {
      continue
    }
    times.push(t)
  }

  if (times.length === 0) {
    return []
  }

  times.sort((a, b) => a.valueOf() - b.valueOf())
  const segments = []
  let segStart = times[0]
  let segEnd = times[0]

  for (let i = 1; i < times.length; i += 1) {
    const t = times[i]
    if (t.diff(segEnd, 'minutes') <= gap) {
      segEnd = t
      continue
    }
    segments.push({
      startAt: segStart.toISOString(),
      endAt: segEnd.toISOString(),
      dashboardUserId: userKey === 'unassigned' ? null : Number(userKey),
      label: 'Active reporting'
    })
    segStart = t
    segEnd = t
  }
  segments.push({
    startAt: segStart.toISOString(),
    endAt: segEnd.toISOString(),
    dashboardUserId: userKey === 'unassigned' ? null : Number(userKey),
    label: 'Active reporting'
  })

  return segments.map((w, index) => ({
    ...w,
    id: `ev-derived-${userKey}-${index}-${w.startAt}`
  }))
}

/**
 * API activity windows plus event-derived presence, merged so gaps with telemetry are not shown as offline.
 */
export function resolveDisplayActivityWindowsForUser(
  userKey,
  apiWindows,
  eventsInRange,
  activityWindowsForAttribution,
  gapMinutes
) {
  const fromEvents = buildActivityWindowsFromEvents(
    userKey,
    eventsInRange,
    gapMinutes,
    activityWindowsForAttribution
  )
  return mergeActivityWindowIntervals(
    [...(apiWindows || []), ...fromEvents],
    gapMinutes
  )
}

export function userHasTelemetrySignalsInRange(
  userKey,
  eventsInRange,
  displayWindows,
  activityWindowsForAttribution
) {
  if (displayWindows?.length > 0) {
    return true
  }
  for (const ev of eventsInRange || []) {
    if (
      eventMatchesTimelineFilterWithSessionAttribution(
        ev,
        userKey,
        activityWindowsForAttribution
      )
    ) {
      return true
    }
  }
  return false
}
